import anime from 'animejs';
import raf from '../raf';
import './moving-image.scss';

function invertOdd(number, index) {
  return index % 2 === 0 ? number : -number;
}

function interpolate(x, a, b) {
  return a + (b - a) * x;
}

const wrapper = document.querySelector('.moving-image');
const scaler = wrapper.querySelector('.moving-image__scaler');
const clippers = wrapper.querySelectorAll('.moving-image__clipper');
const clippersInner = wrapper.querySelectorAll('.moving-image__clipper-inner');
const revealers = wrapper.querySelectorAll('.moving-image__revealer');
const bgs = wrapper.querySelectorAll('.moving-image__bg');
const headerLogo = document.querySelector('.header-logo');
anime.set(bgs[0], { opacity: 0.7 });
anime.set(bgs[1], { opacity: 0.85 });

const movingImage = {};
const REV_DUR = 1000;
let isRevealing = false;
let isRevealingFullscreen = false;
let isExpanded = false;
let x = 0;
let y = 0;
let targetX = 0;
let targetY = 0;
let currentScaleX = 1;
let currentScaleY = 1;
let w = wrapper.clientWidth;
let h = wrapper.clientHeight;
let progress = 0;
let expandAnim = null;

function animateRevealers(scaleFrom, options = {}) {
  const scaleTween = [scaleFrom, 1];
  return anime({
    ...options,
    targets: revealers,
    scale: scaleTween,
    translateX: (el, i) => [0, `${invertOdd(-0.25, i)}%`],
    translateY: (el, i) => [0, `${invertOdd(-0.25, i)}%`],
    update: (a) => {
      const b = -0.32 - 0.2 * currentScaleX;
      const progress2 = (1 + b)
        / (interpolate(a.animations[0].currentValue, ...scaleTween) + b);
      anime.set(bgs, { scale: progress2 });
      if (a.reversed ? a.progress === 0 : a.progress === 100) {
        isRevealing = false;
        if (a.progress === 0) {
          anime.set(revealers, { backgroundColor: 'transparent' });
        }
      }
    },
  });
}

const revAnim = animateRevealers(0.3, {
  autoplay: false,
  duration: REV_DUR,
  easing: 'cubicBezier(.3,0,.5,1)',
});

const revAnimFullscreen = animateRevealers(0.5, {
  autoplay: false,
  complete: () => {
    headerLogo.style.pointerEvents = '';
    isRevealingFullscreen = false;
  },
  duration: 1400,
  easing: 'cubicBezier(.5,0,0,1)',
});

function toggleDirection(isForward) {
  if (isForward === revAnim.reversed) {
    revAnim.reverse();
  }
}

// TODO: cache clientWidth
function getFactor() {
  const ww = wrapper.clientWidth;
  if (ww < 640) {
    return 640 / ww;
  }
  if (ww > 1440) {
    return 1440 / ww;
  }
  return 1;
}

function getScaleX() {
  return getFactor() * 568 / 1440;
}

function getScaleY() {
  return 776 / 900;
}

function translate(tx, ty) {
  if (isExpanded) return;
  anime.set(wrapper, { translateX: tx, translateY: ty });
}

function updateSkew() {
  const ww = w * currentScaleX;
  const hh = h * currentScaleY;
  const rad = Math.atan2(hh, ww);
  const deg = 180 * rad / Math.PI;
  const origin = interpolate(progress, 0.45 * getFactor(), 1);
  anime.set(clippers, {
    skewY: -deg,
    transformOrigin: (el, i) => `${50 * (1 - invertOdd(origin, i))}%`,
  });
  anime.set(clippersInner, {
    skewY: deg,
    transformOrigin: (el, i) => `${50 * (1 - invertOdd(origin, i))}%`,
  });
}

function updateScale() {
  if (isExpanded) return;
  const scaleX = getScaleX();
  const scaleY = getScaleY();
  currentScaleX = scaleX;
  currentScaleY = scaleY;
  anime.set(wrapper, { scaleX, scaleY });
  anime.set(scaler, { scaleX: 1 / scaleX, scaleY: 1 / scaleY });
}

function updateOrigins() {
  anime.set(revealers, { transformOrigin: (el, i) => `${50 * (1 + 1.5 * invertOdd(currentScaleX, i))}% ${50 * (1 + 0.5 * invertOdd(currentScaleY, i))}%` });
  anime.set(bgs, { transformOrigin: (el, i) => `${50 * (1 + 1.5 * invertOdd(currentScaleX, i))}% ${50 * (1 + 0.5 * invertOdd(currentScaleY, i))}%` });
}


const isExpanding = () => expandAnim && !expandAnim.completed;

movingImage.isAnimating = () => isRevealing || isRevealingFullscreen || isExpanding();

movingImage.setImage = (src) => {
  anime.set(revealers, { backgroundColor: 'black' });
  anime.set(bgs, { backgroundImage: `url(${src})` });
};

movingImage.reveal = () => {
  if (isExpanded) return;
  isRevealing = true;
  toggleDirection(true);
};

movingImage.unreveal = () => {
  if (isExpanding() || isExpanded) return;
  isRevealing = true;
  revAnim.seek(Math.min(revAnim.progress * 0.01 * REV_DUR, 600));
  toggleDirection(false);
};

movingImage.setPosition = (_x, _y) => {
  if (!isExpanded) {
    targetX = _x;
    targetY = _y;
  }
};

movingImage.expand = () => {
  expandAnim = anime({
    targets: wrapper,
    scaleX: 1,
    scaleY: 1,
    translateX: 0,
    translateY: 0,
    duration: 1400,
    easing: 'cubicBezier(.7,0,0,1)',
    update: (e) => {
      const [sx, sy] = e.animations;
      const sxInv = 1 / sx.currentValue;
      const syInv = 1 / sy.currentValue;
      anime.set(scaler, { scaleX: sxInv, scaleY: syInv });
      currentScaleX = sx.currentValue;
      currentScaleY = sy.currentValue;
      progress = (sx.currentValue - getScaleX()) / (1 - getScaleX());
      updateSkew();
    },
  });
};

movingImage.setScrollbarWidth = (sw) => {
  if (sw !== 0) {
    anime.set(wrapper, { width: `calc(100vw - ${sw}px)` });
  }
};

raf((t) => {
  const cw = wrapper.clientWidth;
  const ch = wrapper.clientHeight;
  if (targetX !== x || targetY !== y || w !== cw || h !== ch) {
    translate(targetX - 0.5 * cw, targetY - 0.28125 * cw);
    updateScale();
    if (w !== cw || h !== ch) {
      setTimeout(() => {
        updateSkew();
      }, 0);
    }
    x = targetX;
    y = targetY;
    w = cw;
    h = ch;
  }
  if (isRevealing) {
    revAnim.tick(t);
  }
  if (isRevealingFullscreen) {
    revAnimFullscreen.tick(t);
  }
});

function instantExpand() {
  anime.set(wrapper, {
    scaleX: 1,
    scaleY: 1,
    translateX: 0,
    translateY: 0,
  });
  anime.set(scaler, { scaleX: 1, scaleY: 1 });
  currentScaleX = 1;
  currentScaleY = 1;
  isExpanded = true;
  progress = 1;
  updateSkew();
  updateOrigins();
}

movingImage.instantFullscreen = () => {
  instantExpand();
  anime.set(revealers, { scale: 1 });
  anime.set(bgs, { scale: 1 });
};

movingImage.reset = () => {
  if (movingImage.isAnimating()) return;
  isRevealing = false;
  isExpanded = false;
  progress = 0;
  toggleDirection(true);
  translate(0, 0);
  updateScale();
  updateSkew();
  updateOrigins();
  revAnim.restart();
  revAnim.pause();
  revAnim.seek(1);
  revAnim.tick();
  anime.set(revealers, { backgroundColor: 'transparent' });
};

movingImage.revealFullscreen = () => {
  instantExpand();
  headerLogo.style.pointerEvents = 'none';
  isRevealingFullscreen = true;
  revAnimFullscreen.restart();
  revAnimFullscreen.pause();
};

export default movingImage;

window.movingImage = movingImage;
